import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Fingerprint, Target, Lightbulb, AlertTriangle, HelpCircle, Cpu, ArrowRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EvidenceTooltip } from "./EvidenceTooltip";

interface ClassificationProvenance {
  source: 'user_override' | 'system_confirmed';
  system_suggestion: {
    type: string;
    subtype: string;
    confidence: number;
    model?: string | null;
    input_hash?: string | null;
  };
  final: {
    type: string;
    subtype: string;
  };
  recorded_at?: string | null;
}

interface DecisionSnapshotProps {
  data: {
    problem_statement?: string;
    decision_question?: string;
    decision_question_original?: string;
    primary_tension?: string;
    primary_hypothesis?: string;
  };
  classificationProvenance?: ClassificationProvenance | null;
  evidence?: { source_refs?: string[]; data_ids?: Record<string, any> };
}

/**
 * Safely extract human-readable text from decision_question.
 * Phase 1.4 sometimes stores a JSON object (Decision Thesis) as user_answer.
 * This function extracts the `kernsatz` field if present, otherwise returns plain text.
 */
function parseDecisionQuestion(raw: string | undefined | null): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  // Detect JSON (starts with { or ")
  if (trimmed.startsWith("{") || trimmed.startsWith('"{')) {
    try {
      let parsed = JSON.parse(trimmed);
      // Handle double-encoded JSON strings
      if (typeof parsed === "string" && parsed.startsWith("{")) {
        parsed = JSON.parse(parsed);
      }
      if (typeof parsed === "object" && parsed !== null) {
        // Extract kernsatz (the core sentence of the decision thesis)
        return parsed.kernsatz || parsed.decision_question || parsed.text || trimmed;
      }
    } catch {
      // Not valid JSON — return as-is
    }
  }
  return trimmed;
}

export function DecisionSnapshot({ data, classificationProvenance, evidence }: DecisionSnapshotProps) {
  if (!data) return null;

  const rawQuestion = data.decision_question_original || data.decision_question;
  const decisionQuestion = parseDecisionQuestion(rawQuestion);
  const isOverride = classificationProvenance?.source === 'user_override';

  return (
    <Card id="decision-snapshot" className="scroll-mt-24 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Fingerprint className="h-5 w-5 text-primary" />
            <EvidenceTooltip evidence={evidence}>Decision Snapshot</EvidenceTooltip>
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-xs bg-primary/10 border-primary/30 cursor-help">
                  Ankerdokument
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end" className="max-w-xs">
                <p className="text-xs">Diese Elemente definieren den Entscheidungsrahmen und werden im gesamten Report referenziert.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Aus Nutzereingabe, nicht KI-generiert
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Problemkern */}
        {data.problem_statement && (
          <div className="user-input">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h4 className="font-semibold text-sm">Problemkern</h4>
              <Badge variant="secondary" className="text-[10px] ml-auto">Nutzereingabe</Badge>
            </div>
            <p className="text-sm leading-relaxed pl-6">{data.problem_statement}</p>
          </div>
        )}

        {/* Spannungsfeld */}
        {data.primary_tension && (
          <div className="system-derived bg-muted/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-orange-500" />
              <h4 className="font-semibold text-sm">Spannungsfeld</h4>
              <Badge variant="outline" className="text-[10px] ml-auto">Systemisch abgeleitet</Badge>
            </div>
            <p className="text-sm leading-relaxed pl-6">{data.primary_tension}</p>
          </div>
        )}

        {/* Kernhypothese */}
        {data.primary_hypothesis && (
          <div className="user-input bg-primary/10 rounded-lg p-4 border-l-4 border-primary">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-primary" />
              <h4 className="font-semibold text-sm text-primary">Kernhypothese</h4>
              <Badge variant="secondary" className="text-[10px] ml-auto">Nutzereingabe</Badge>
            </div>
            <p className="text-sm font-medium leading-relaxed">{data.primary_hypothesis}</p>
          </div>
        )}

        {/* Entscheidungsfrage */}
        {decisionQuestion && (
          <div className="user-input bg-gradient-to-r from-primary/20 to-primary/10 rounded-lg p-4 border border-primary/30">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              <h4 className="font-semibold text-sm text-primary">Kernfrage der Entscheidung</h4>
              <Badge className="text-[10px] ml-auto bg-primary/20 text-primary border-0">
                1:1 aus Phase 1
              </Badge>
            </div>
            <p className="text-base font-semibold leading-relaxed">
              "{decisionQuestion}"
            </p>
            <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-primary/20 italic">
              Diese Frage beantwortet der folgende Report.
            </p>
          </div>
        )}

        {/* Classification Provenance */}
        {classificationProvenance && (
          <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Cpu className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold text-sm">Klassifizierungs-Provenance</h4>
              <Badge
                variant="outline"
                className={`text-[10px] ml-auto ${
                  isOverride
                    ? "border-amber-500/40 text-amber-600 bg-amber-500/10"
                    : "border-emerald-500/40 text-emerald-600 bg-emerald-500/10"
                }`}
              >
                {isOverride ? "User-Override" : "System bestätigt"}
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {/* System suggestion */}
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium uppercase tracking-wide text-[10px]">
                  System-Vorschlag
                </p>
                <p className="text-foreground">
                  {classificationProvenance.system_suggestion.type} / {classificationProvenance.system_suggestion.subtype}
                </p>
                <p className="text-muted-foreground">
                  Konfidenz: {classificationProvenance.system_suggestion.confidence}%
                </p>
              </div>

              {/* Final selection */}
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium uppercase tracking-wide text-[10px]">
                  Finale Auswahl
                </p>
                <p className="text-foreground">
                  {classificationProvenance.final.type} / {classificationProvenance.final.subtype}
                </p>
                {isOverride && (
                  <div className="flex items-center gap-1 text-amber-600">
                    <ArrowRight className="h-3 w-3" />
                    <span>Manuell korrigiert</span>
                  </div>
                )}
              </div>
            </div>

            {/* Model & hash footer */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pt-2 border-t border-border/50 text-[10px] text-muted-foreground">
              {classificationProvenance.system_suggestion.model && (
                <span>Modell: {classificationProvenance.system_suggestion.model}</span>
              )}
              {classificationProvenance.system_suggestion.input_hash && (
                <span>Hash: {classificationProvenance.system_suggestion.input_hash.slice(0, 12)}…</span>
              )}
              {classificationProvenance.recorded_at && (
                <span>Erfasst: {new Date(classificationProvenance.recorded_at).toLocaleDateString('de-DE')}</span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}